import { Spot, UserLocation } from '../types/spot';

/**
 * Universal Map Linking Service for Naver Maps, KakaoMap and Google Maps
 * Handles Web URLs, Mobile App URI Schemes, and Transit Directions
 */

export interface MapLinks {
  kakaoWeb: string;
  kakaoApp: string;
  kakaoRouteWeb: string;
  kakaoRouteApp: string;
  naverWeb: string;
  naverApp: string;
  naverRouteWeb: string;
  naverRouteApp: string;
  googleWeb: string;
}

const SOCIAL_EVENT_KAKAO_VENUES: Record<string, string> = {
  // DDP, Naksan Park & Indian / Veggie Restaurants
  'seoul-ddp-dongdaemun': '동대문디자인플라자',
  'seoul-naksan-park-fortress': '낙산공원',
  'food-seoul-everest-dongdaemun': '에베레스트 동대문본점',
  'food-seoul-kali-daehangno': '깔리 대학로점',
  'food-seoul-manakamana-daehangno': '머노까머나 대학로점',
  'food-seoul-everest-curry-world-dongdaemun': '에베레스트 커리월드',
  'food-seoul-pooja-dongdaemun': '뿌자 동대문',
  'food-seoul-persian-palace-hyehwa': '페르시안궁전',
  'event-social-seoul-pub-crawl': '홍대입구역 9번출구',
  'event-social-yna-hongdae-language-exchange': '서울 마포구 와우산로 65',
  'event-social-dialogue-hbc-language-exchange': '서울 용산구 신흥로 31',
  'event-social-seoulshare-speed-dating': '인사동 코트',
  'event-social-gsm-speed-dating': '상수역 1번출구 GSM테라스',
  'event-social-gsm-hongdae-terrace': 'GSM테이블',
  'event-social-itaewon-international-singles': '이태원역 1번출구',
  'event-social-redbutton-hongdae-boardgames': '레드버튼 홍대점',
  'event-social-songdo-igc-pyeonmaek-night': '송도 아메리칸타운더샵',
  'event-social-incheon-bupyeong-language-exchange': '쿠우쿠우 골드 부평점',
  'event-social-songdo-igc-international-social': '캠퍼스타운역',
  'event-social-seoul-sunset-hike-chimaek': '인왕산 둘레길',
  'event-social-gsm-gangnam-singles': '신논현역 3번출구 GSM테이블',
  'event-social-thursday-seoul-singles': '이태원역',
  'event-social-culcom-sinchon-hongdae-cafe': '컬컴 신촌점',
  'event-social-jongno-gangnam-conversation-cafe': '종각역 4번출구',
  'event-social-boardgames-with-koreans-hangang': '뚝섬한강공원',
  'event-social-maju-expat-korean-matchmaking': '연남동 경의선숲길',
  'event-social-soohouse-seoul-singles-mixer': '홍대입구역 9번출구',
  // Verified Activity & Outdoor POIs for 100% Kakao/Naver Map accuracy
  'activity-gapyeong-topland-bungee': '가평탑랜드',
  'activity-yongin-flystation-skydiving': '플라이스테이션',
  'activity-seoul-lotte-skybridge-tour': '서울스카이',
  'activity-yangpyeong-paragliding-park': '양평패러글라이딩파크',
  'activity-incheon-ganghwa-mega-luge': '강화씨사이드리조트루지',
  'activity-incheon-muuido-sea-sky-zipline': '하나개해수욕장 씨스카이월드',
  'activity-chungju-skydive-korea': '스카이다이브코리아',
  'activity-seoul-crazychute-minigolf': '크레이지슈트',
  'activity-seoul-golfzon-screen-golf': '골프존파크 강남 테헤란로점',
  'activity-seoul-coex-aquarium': '코엑스 아쿠아리움',
  'activity-seoul-gangnam-real-gun-shooting': '강남실탄사격장',
  'activity-seoul-banpo-tubester-boat': '세빛섬 튜브스터',
  'activity-seoul-portal-pc-arena-gangnam': '포탈 PC 강남점',
  'activity-seoul-smashing-bowl-gangnam': '스매싱볼 강남점',
  'activity-seoul-zero-world-gangnam': '제로월드 강남점',
  'activity-goyang-smob-sports-monster': '스몹 스타필드고양점',
  'activity-seoul-robinhood-archery': '로빈훗 홍대점',
  'activity-seoul-dynamic-maze': '다이나믹메이즈 인사동점',
  'activity-seoul-key-escape-hongdae': '키에스케이프 홍대점',
  'activity-seoul-the-climb-hongdae': '더클라임 클라이밍 연남점',
  'activity-seoul-ttukseom-hangang-kayak': '뚝섬한강공원 수상레저',
  'activity-seoul-lotte-world-ice-garden': '롯데월드 아이스링크',
  'activity-seoul-roller-city': '롤러시티',
  'activity-jeju-981-park': '9.81파크 제주',
  'activity-busan-diamond-bay-yacht': '다이아몬드베이',
  // Verified Festival & Cultural Event POIs
  'event-suwon-hwaseong-media-art': '수원화성 창룡문',
  'event-hangang-drone-light-show': '뚝섬한강공원 수변무대',
  'event-seoul-silver-grass-festival': '하늘공원',
  'event-the-hyundai-christmas-village': '더현대 서울',
  'event-seoul-lantern-festival': '광화문광장',
  'event-seoul-light-ddp-winter': '동대문디자인플라자',
  'event-seoul-plaza-ice-skating': '서울광장 스케이트장',
  'event-show-myeongdong-nanta': '명동난타극장',
  'event-show-the-painters-gwanghwamun': '경향아트힐',
  'event-bosingak-new-year-countdown': '보신각',
  'event-incheon-sky-festival': '인스파이어 아레나',
  'event-nowon-moonlight-walk': '당현천 바닥분수',
  'event-hwadam-forest-autumn': '화담숲',
  'event-yangpyeong-yongmunsan-ginkgo': '용문사 은행나무',
  'event-jogyesa-chrysanthemum-festival': '조계사',
  'event-jamsugyo-car-free-festival': '잠수교',
  'event-nowon-craft-beer-festival': '노원 화랑대철도공원',
  'event-do-ho-suh-mmca': '국립현대미술관 서울',
  'event-seoul-outdoor-library': '서울광장',
  // Verified Fitness, Gym & Outdoor Street Workout POIs
  'fitness-gymboxx-gangnam-1': '짐박스피트니스 강남1호점',
  'fitness-gymboxx-gangnam-2': '짐박스피트니스 강남2호점',
  'fitness-spoany-gangnam-1': '스포애니 강남역1호점',
  'fitness-ablegym-gangnam': '에이블짐 강남역점',
  'fitness-outdoor-ttukseom-hangang': '뚝섬한강공원',
  'fitness-outdoor-yeouido-hangang': '여의도한강공원',
  'fitness-outdoor-namsan-sanseujang': '장충체육회',
  'fitness-outdoor-seoul-forest': '서울숲',
  'fitness-outdoor-banpo-hangang': '반포한강공원 달빛광장',
  'fitness-outdoor-songdo-global-park': '송도 글로벌파크',
  'fitness-outdoor-yanghwa-hangang': '양화한강공원',
  'fitness-indoor-smob-sports-monster': '스몹 스타필드 고양',
  'fitness-outdoor-incheon-bupyeong-park': '부평공원',
  // Extraordinary Attractions 1-2h from Incheon
  'spot-gyeonggi-pocheon-art-valley': '포천아트밸리',
  'spot-incheon-ganghwa-gyodongdo-dmz': '교동도 대룡시장',
  'spot-incheon-ganghwa-joyang-bangjik': '조양방직',
  'spot-incheon-seonjaedo-mokseom': '선재도 목섬',
  'spot-gyeonggi-siheung-baegot-oido': '배곧한울공원 해수체험장',
  'spot-gangwon-gangneung-haslla-art-world': '하슬라아트월드',
  'spot-gyeonggi-pocheon-hantangang-geopark': '포천 한탄강 하늘다리',
  'spot-gyeonggi-yongin-daejanggeum-park': '용인 대장금파크',
  'spot-gyeonggi-gwangmyeong-cave': '광명동굴',
  'activity-gyeonggi-jebudo-marine-cable-car': '서해랑 제부도 해상케이블카',
};

/**
 * Sanitizes Korean place names so that Naver and Kakao search engines
 * find the exact POI without failing on slashes, ampersands, dashes, or brackets.
 */
export function getCleanSpotKoreanName(nameKr: string, spotId?: string): string {
  if (spotId && SOCIAL_EVENT_KAKAO_VENUES[spotId]) {
    return SOCIAL_EVENT_KAKAO_VENUES[spotId];
  }
  if (!nameKr) return '';
  
  // 1. Remove bracketed details: e.g. "부평 모두몰 (지하상가)" -> "부평 모두몰", "N서울타워 (남산타워)" -> "N서울타워"
  let clean = nameKr.replace(/\s*\([^)]*\)/g, '').trim();

  // 2. Strip long event subtitle descriptions after en-dash or hyphen
  if (clean.includes('–') || clean.includes(' - ')) {
    const parts = clean.split(/\s*[–-]\s*/).map((p) => p.trim()).filter(Boolean);
    clean = parts[0] || clean;
  }

  // 3. Handle slashes: take the primary or more specific landmark (e.g. "월미도 / 월미테마파크" -> "월미테마파크")
  if (clean.includes('/')) {
    const parts = clean.split('/').map((p) => p.trim()).filter(Boolean);
    clean = parts[parts.length - 1] || parts[0] || clean;
  }

  // 4. Handle ampersands: take the primary landmark (e.g. "송도 트리플스트리트 & 현대아울렛" -> "송도 트리플스트리트")
  if (clean.includes('&')) {
    const parts = clean.split('&').map((p) => p.trim()).filter(Boolean);
    clean = parts[0] || clean;
  }

  return clean.trim();
}

export function getSpotMapLinks(spot: Spot, userLocation?: UserLocation): MapLinks {
  const cleanName = getCleanSpotKoreanName(spot.nameKr, spot.id) || spot.nameKr || spot.name;
  const encName = encodeURIComponent(cleanName);
  const lat = spot.lat;
  const lng = spot.lng;

  // 1. KakaoMap Links (Opens official Kakao place entity with opening hours & reviews)
  // Web Place view: Highlights the official place entity with photos, opening hours & reviews
  const kakaoWeb = `https://map.kakao.com/link/search/${encName}`;
  // App scheme (Mobile): Searches and opens official place directly in KakaoMap app
  const kakaoApp = `kakaomap://search?q=${encName}`;
  // Web Transit Directions
  const kakaoRouteWeb = `https://map.kakao.com/link/to/${encName},${lat},${lng}`;
  // App Transit Directions
  const kakaoRouteApp = userLocation
    ? `kakaomap://route?sp=${userLocation.lat},${userLocation.lng}&ep=${lat},${lng}&by=PUBLICTRANSIT`
    : `kakaomap://route?ep=${lat},${lng}&by=PUBLICTRANSIT`;

  // 2. Naver Map Links (Official modern Naver place search and transit)
  // Web Place view: Opens official Naver Place card with opening hours & visitor reviews
  const naverWeb = `https://map.naver.com/p/search/${encName}`;
  // App scheme (Mobile): Searches and opens official place in Naver Map app
  const naverApp = `nmap://search?query=${encName}&appname=korea.spotfinder`;
  // Web Transit Directions
  const naverRouteWeb = userLocation
    ? `https://map.naver.com/p/directions/${userLocation.lng},${userLocation.lat},${encodeURIComponent('내 위치')},,/${lng},${lat},${encName},,PLACE_POI/-/transit?c=14.00,0,0,0,dh`
    : `https://map.naver.com/p/directions/-/${lng},${lat},${encName},,PLACE_POI/-/transit?c=14.00,0,0,0,dh`;
  // App Transit Directions
  const naverRouteApp = `nmap://route/public?dlat=${lat}&dlng=${lng}&dname=${encName}&appname=korea.spotfinder`;

  // 3. Google Maps Link (Always accurate with exact lat/lng)
  const googleWeb = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return {
    kakaoWeb,
    kakaoApp,
    kakaoRouteWeb,
    kakaoRouteApp,
    naverWeb,
    naverApp,
    naverRouteWeb,
    naverRouteApp,
    googleWeb,
  };
}

/**
 * Intelligent Smart Opener that checks device type and launches app or falls back to web
 */
export function openKakaoMap(spot: Spot, route: boolean = false, userLocation?: UserLocation): void {
  const links = getSpotMapLinks(spot, userLocation);
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isMobile) {
    const appUrl = route ? links.kakaoRouteApp : links.kakaoApp;
    const webUrl = route ? links.kakaoRouteWeb : links.kakaoWeb;
    tryOpenWithFallback(appUrl, webUrl);
  } else {
    window.open(route ? links.kakaoRouteWeb : links.kakaoWeb, '_blank', 'noopener,noreferrer');
  }
}

export function openNaverMap(spot: Spot, route: boolean = false, userLocation?: UserLocation): void {
  const links = getSpotMapLinks(spot, userLocation);
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isMobile) {
    const appUrl = route ? links.naverRouteApp : links.naverApp;
    const webUrl = route ? links.naverRouteWeb : links.naverWeb;
    tryOpenWithFallback(appUrl, webUrl);
  } else {
    window.open(route ? links.naverRouteWeb : links.naverWeb, '_blank', 'noopener,noreferrer');
  }
}

function tryOpenWithFallback(appUrl: string, fallbackUrl: string) {
  let appOpened = false;
  const markOpened = () => {
    if (document.visibilityState === 'hidden') appOpened = true;
  };
  document.addEventListener('visibilitychange', markOpened);
  window.addEventListener('pagehide', markOpened);

  window.location.href = appUrl;

  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', markOpened);
    window.removeEventListener('pagehide', markOpened);
    // Page still visible → the app is not installed; continue in the same tab
    // (a delayed window.open would be blocked as a popup on iOS/Android).
    if (!appOpened && document.visibilityState === 'visible') {
      window.location.href = fallbackUrl;
    }
  }, 1500);
}

/**
 * Multi-spot route link generator for 1-day itinerary planning
 */
export function generateMultiSpotRouteUrl(spots: Spot[], mapType: 'kakao' | 'naver'): string {
  if (spots.length === 0) return '';
  if (spots.length === 1) {
    const s = spots[0];
    const clean = getCleanSpotKoreanName(s.nameKr) || s.nameKr;
    return mapType === 'kakao'
      ? `https://map.kakao.com/link/map/${encodeURIComponent(clean)},${s.lat},${s.lng}`
      : `https://map.naver.com/p/search/${encodeURIComponent(clean)}?c=15.00,0,0,0,dh`;
  }

  const origin = spots[0];
  const dest = spots[spots.length - 1];
  const originClean = getCleanSpotKoreanName(origin.nameKr) || origin.nameKr;
  const destClean = getCleanSpotKoreanName(dest.nameKr) || dest.nameKr;

  if (mapType === 'kakao') {
    // Kakao transit route from origin to destination coordinates
    return `https://map.kakao.com/link/to/${encodeURIComponent(destClean)},${dest.lat},${dest.lng}`;
  } else {
    // Naver transit directions
    return `https://map.naver.com/p/directions/${origin.lng},${origin.lat},${encodeURIComponent(originClean)},,PLACE_POI/${dest.lng},${dest.lat},${encodeURIComponent(destClean)},,PLACE_POI/-/transit`;
  }
}

